import React, { useState, useRef, useEffect, useCallback } from 'react';
import './App.css';

function App() {
  const [dateRange, setDateRange] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availabilityData, setAvailabilityData] = useState(null);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [contentData, setContentData] = useState(null);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [blockedDates, setBlockedDates] = useState({});
  const [monthAvailability, setMonthAvailability] = useState({});
  const [showIframe, setShowIframe] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [roomImages, setRoomImages] = useState({});
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const calendarRef = useRef(null);

  // Hardcoded config to avoid import issues
  const config = {
    header: {
      logo: "Check Availability",
      navigation: [
        { label: "Features", href: "#features" },
        { label: "About", href: "#about" },
      ],
      ctaButton: { text: "Check Now" }
    },
    hero: {
      title: "Check Room Availability",
      subtitle: "Find available rooms for your preferred dates",
      buttons: [
        { text: "Check Availability", type: "primary" },
        { text: "Learn More", type: "secondary" }
      ]
    },
    features: {
      title: "Why Choose Check Availability?",
      items: [
        { icon: "📅", title: "Easy Date Selection", description: "Simple calendar interface to pick your dates" },
        { icon: "🔍", title: "Real-time Results", description: "Instant availability checking with live data" },
        { icon: "📱", title: "Mobile Friendly", description: "Works perfectly on all devices and screen sizes" },
        { icon: "⚡", title: "Fast & Reliable", description: "Quick results with reliable availability data" }
      ]
    },
    datePicker: {
      title: "Select Date Range",
      placeholder: "Select date range...",
      clearButtonText: "✕",
      applyButtonText: "Apply",
      weekdays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      propertyBBIDs: ["20001"]
    },
    footer: {
      copyright: "© 2025 Check Availability. All rights reserved."
    },
    api: {
      availabilityWorker: "https://checkavailability.alldigital.workers.dev",
      contentWorker: "https://getcontent.alldigital.workers.dev",
      imageWorker: "https://getimages.alldigital.workers.dev"
    },
    booking: {
      propertyId: "20001",
      nbid: "371",
      baseUrl: "https://book.nightsbridge.com"
    }
  };

  // Set default dates (next weekend)
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to midnight
    
    const nextWeekend = new Date(today);
    nextWeekend.setDate(today.getDate() + (7 - today.getDay() + 5) % 7); // Next Friday
    nextWeekend.setHours(0, 0, 0, 0); // Set to midnight
    
    const endDate = new Date(nextWeekend);
    endDate.setDate(nextWeekend.getDate() + 7); // 7 nights for better availability
    endDate.setHours(0, 0, 0, 0); // Set to midnight
    
    setSelectedStartDate(nextWeekend);
    setSelectedEndDate(endDate);
    setDateRange(`${nextWeekend.getFullYear()}-${String(nextWeekend.getMonth() + 1).padStart(2, '0')}-${String(nextWeekend.getDate()).padStart(2, '0')} - ${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`);
  }, []);

  const fetchAvailability = useCallback(async (start, end) => {
    if (!start || !end || isFetching) {
      console.log('Skipping fetch - missing dates or already fetching');
      return;
    }

    console.log('Starting availability fetch for:', start, 'to', end);
    setIsFetching(true);
    setIsLoadingAvailability(true);
    setError(null);

    try {
      // Get all BBIDs to check
      const bbids = config.datePicker.propertyBBIDs || ["20001"];
      console.log('Checking availability for BBIDs:', bbids);
      
      // Fetch availability for all BBIDs
      const availabilityPromises = bbids.map(async (bbid) => {
        const requestBody = {
          bbid: bbid,
          startdate: start,
          enddate: end
        };

        console.log(`Sending availability request for BBID ${bbid}:`, requestBody);
        
        const response = await fetch(config.api.availabilityWorker, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });

        console.log(`Availability Response status for BBID ${bbid}:`, response.status);
        
        const data = await response.json();
        console.log(`Availability data received for BBID ${bbid}:`, data);
        
        return { bbid, data };
      });

      // Wait for all requests to complete
      const results = await Promise.all(availabilityPromises);
      console.log('All availability results:', results);
      
      // Combine results - if any BBID has available rooms, show them
      const successfulResults = results.filter(result => result.data.success && result.data.data?.roomtypes);
      
      if (successfulResults.length > 0) {
        // Use the first successful result as the main data, but combine room types
        const mainResult = successfulResults[0];
        const combinedData = {
          ...mainResult.data,
          data: {
            ...mainResult.data.data,
            roomtypes: successfulResults.flatMap(result => 
              result.data.data.roomtypes.map(room => ({
                ...room,
                bbid: result.bbid,
                propertyName: result.data.data.name
              }))
            )
          }
        };
        
        console.log('Combined availability data:', combinedData);
        setAvailabilityData(combinedData);
      } else {
        // No successful results
        setAvailabilityData({ success: false, error: { code: 0, message: 'No availability found for any property' } });
      }
      
      // Fetch content data for all BBIDs
      try {
        console.log('Fetching content data for BBIDs:', bbids);
        
        const contentPromises = bbids.map(async (bbid) => {
          try {
            const contentResponse = await fetch(config.api.contentWorker, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
              },
              body: JSON.stringify({
                bbid: bbid
              })
            });

            console.log(`Content API Response status for BBID ${bbid}:`, contentResponse.status);
            
            if (!contentResponse.ok) {
              throw new Error(`Content API error: ${contentResponse.status} ${contentResponse.statusText}`);
            }
            
            const contentData = await contentResponse.json();
            console.log(`Content API Response data for BBID ${bbid}:`, contentData);
            
            return { bbid, contentData };
          } catch (error) {
            console.error(`Error fetching content for BBID ${bbid}:`, error);
            return { bbid, contentData: { success: false, error: error.message } };
          }
        });

        const contentResults = await Promise.all(contentPromises);
        console.log('All content results:', contentResults);
        
        // Combine content data
        const successfulContentResults = contentResults.filter(result => result.contentData.success);
        
        if (successfulContentResults.length > 0) {
          const combinedContentData = {
            success: true,
            data: {
              roomtypes: successfulContentResults.flatMap(result => 
                result.contentData.data?.roomtypes?.map(room => ({
                  ...room,
                  bbid: result.bbid
                })) || []
              )
            }
          };
          
          console.log('Combined content data:', combinedContentData);
          setContentData(combinedContentData);
        } else {
          console.log('No content data available, using basic room info');
          setContentData({ success: false, error: 'No content data available' });
        }
      } catch (contentError) {
        console.error('Error fetching content data:', contentError);
        setContentData({ 
          error: `Failed to fetch content: ${contentError.message}`,
          success: false 
        });
      }
      
      // Fetch room images for all BBIDs
      try {
        console.log('Fetching room images for BBIDs:', bbids);
        
        const imagePromises = bbids.map(async (bbid) => {
          try {
            const imageResponse = await fetch(config.api.imageWorker, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
              },
              body: JSON.stringify({
                bblist: [parseInt(bbid)]
              })
            });

            console.log(`Image API Response status for BBID ${bbid}:`, imageResponse.status);
            
            if (!imageResponse.ok) {
              throw new Error(`Image API error: ${imageResponse.status} ${imageResponse.statusText}`);
            }
            
            const imageData = await imageResponse.json();
            console.log(`Image API Response data for BBID ${bbid}:`, imageData);
            
            return { bbid, imageData };
          } catch (error) {
            console.error(`Error fetching images for BBID ${bbid}:`, error);
            return { bbid, imageData: { success: false, error: error.message } };
          }
        });

        const imageResults = await Promise.all(imagePromises);
        console.log('All image results:', imageResults);
        
        // Combine image data
        const successfulImageResults = imageResults.filter(result => {
          const isSuccess = result.imageData.success;
          const hasErrorCode = result.imageData.error && result.imageData.error.code === 0;
          const hasData = result.imageData.data && Object.keys(result.imageData.data).length > 0;
          
          // Consider successful if success is true AND (error code is 0 OR we have data)
          return isSuccess && (hasErrorCode || hasData);
        });
        
        console.log('Successful image results:', successfulImageResults.length);
        
        if (successfulImageResults.length > 0) {
          const combinedImageData = {};
          
          successfulImageResults.forEach(result => {
            console.log(`Processing image data for BBID ${result.bbid}`);
            
            // Check for roomtypegalleries first (expected format)
            if (result.imageData.data?.roomtypegalleries) {
              result.imageData.data.roomtypegalleries.forEach(roomGallery => {
                if (roomGallery.roomtypeid && roomGallery.gallery && roomGallery.gallery.length > 0) {
                  // Convert roomtypeid to string to match our room.rtid format
                  const roomtypeid = String(roomGallery.roomtypeid);
                  
                  // Only take the first image per room to avoid massive amounts
                  if (!combinedImageData[roomtypeid]) {
                    // Take only the first image from the gallery to avoid loading too many images
                    const firstImage = roomGallery.gallery[0];
                    combinedImageData[roomtypeid] = [firstImage];
                    console.log(`Added image for roomtype ${roomtypeid}`);
                  }
                }
              });
            } 
            // Check for imageslist format (actual API response)
            else if (result.imageData.data?.imageslist) {
              // Process each item in imageslist
              result.imageData.data.imageslist.forEach((item, index) => {
                // Check if this item has roomtypegalleries
                if (item.roomtypegalleries && Array.isArray(item.roomtypegalleries)) {
                  item.roomtypegalleries.forEach(roomGallery => {
                    if (roomGallery.roomtypeid && roomGallery.gallery && roomGallery.gallery.length > 0) {
                      // Convert roomtypeid to string to match our room.rtid format
                      const roomtypeid = String(roomGallery.roomtypeid);
                      
                      // Only take the first image per room to avoid massive amounts
                      if (!combinedImageData[roomtypeid]) {
                        // Take only the first image from the gallery to avoid loading too many images
                        const firstImage = roomGallery.gallery[0];
                        combinedImageData[roomtypeid] = [firstImage];
                        console.log(`Added image for roomtype ${roomtypeid}`);
                      }
                    }
                  });
                }
              });
            }
          });
          
          console.log('Combined image data:', Object.keys(combinedImageData).length, 'rooms');
          setRoomImages(combinedImageData);
        } else {
          console.log('No successful image results');
          setRoomImages({});
        }
      } catch (imageError) {
        console.error('Error fetching room images:', imageError);
        setRoomImages({});
      } finally {
        setIsLoadingAvailability(false);
        setIsFetching(false);
      }
      
    } catch (error) {
      console.error('Error checking availability:', error);
      setError('Failed to fetch availability data');
    } finally {
      setIsLoadingAvailability(false);
      setIsFetching(false);
    }
  }, [config.datePicker.propertyBBIDs, config.api.availabilityWorker, config.api.contentWorker, config.api.imageWorker, isFetching]);

  const handleDateClick = (date) => {
    if (!date || isDateDisabled(date)) return;
    
    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      // First selection or new selection
      setSelectedStartDate(date);
      setSelectedEndDate(null);
      setDateRange(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
      // Don't fetch availability yet - wait for checkout date
      console.log('Check-in date selected, waiting for checkout date');
    } else {
      // Second selection (checkout date)
      if (date <= selectedStartDate) {
        // If selected date is before or same as start date, make it the new start date
        setSelectedStartDate(date);
        setSelectedEndDate(null);
        setDateRange(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
        // Don't fetch availability yet - wait for checkout date
        console.log('New check-in date selected, waiting for checkout date');
      } else {
        // Valid end date selected - now fetch availability
        setSelectedEndDate(date);
        // Format dates without timezone issues
        const start = `${selectedStartDate.getFullYear()}-${String(selectedStartDate.getMonth() + 1).padStart(2, '0')}-${String(selectedStartDate.getDate()).padStart(2, '0')}`;
        const end = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        setDateRange(`${start} - ${end}`);
        setShowCalendar(false);
        
        console.log('Checkout date selected, fetching availability for:', start, 'to', end);
        // Now fetch availability for the selected range
        fetchAvailability(start, end);
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const getNightsCount = () => {
    if (!selectedStartDate || !selectedEndDate) return 0;
    const diffTime = Math.abs(selectedEndDate - selectedStartDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const generateBookingUrl = (startDate, endDate, bbid = null, bbrtid = null) => {
    if (!startDate || !endDate) return '';
    
    // Format date without timezone issues
    const startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
    const nights = getNightsCount();
    
    // Use specific BBID and BBRTID if provided, otherwise use default
    const propertyId = bbid || config.booking.propertyId;
    const roomTypeId = bbrtid ? `&bbrtid=${bbrtid}` : '';
    
    return `${config.booking.baseUrl}/${propertyId}?nbid=${config.booking.nbid}&startdate=${startDateStr}&nights=${nights}${roomTypeId}`;
  };

  const openBookingIframe = (bbid = null, bbrtid = null) => {
    if (!selectedStartDate || !selectedEndDate) {
      alert('Please select both check-in and check-out dates first.');
      return;
    }
    
    const url = generateBookingUrl(selectedStartDate, selectedEndDate, bbid, bbrtid);
    console.log('Selected dates:', {
      startDate: selectedStartDate.toISOString(),
      endDate: selectedEndDate.toISOString(),
      startDateStr: selectedStartDate.toISOString().split('T')[0],
      nights: getNightsCount()
    });
    console.log('Opening booking iframe with URL:', url);
    console.log('Room-specific booking:', { bbid, bbrtid });
    setIframeUrl(url);
    setShowIframe(true);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add all days in the month - create dates at midnight local time
    for (let i = 1; i <= daysInMonth; i++) {
      const dayDate = new Date(year, month, i);
      dayDate.setHours(0, 0, 0, 0); // Set to midnight local time
      days.push(dayDate);
    }
    
    return days;
  };

  const isDateInRange = (date) => {
    if (!selectedStartDate || !selectedEndDate || !date) return false;
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  const isDateSelected = (date) => {
    if (!date) return false;
    return (selectedStartDate && date.getTime() === selectedStartDate.getTime()) ||
           (selectedEndDate && date.getTime() === selectedEndDate.getTime());
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if date is in the past
    if (date < today) return true;
    
    // Check if date is blocked (no availability)
    const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    if (blockedDates[dateKey]) {
      console.log('Date disabled (blocked):', dateKey);
      return true;
    }
    
    return false;
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // Check availability for the current month
  const checkMonthAvailability = useCallback(async (month, year) => {
    const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
    
    // Skip if we already have data for this month
    if (monthAvailability[monthKey]) return;
    
    console.log('Fetching month availability for:', monthKey);
    
    try {
      // Get first and last day of the month
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      // Get all BBIDs to check
      const bbids = config.datePicker.propertyBBIDs || ["20001"];
      
      // Fetch availability for all BBIDs
      const availabilityPromises = bbids.map(async (bbid) => {
        const requestBody = {
          bbid: bbid,
          startdate: firstDay.toISOString().split('T')[0],
          enddate: lastDay.toISOString().split('T')[0]
        };

        console.log(`Month availability request for BBID ${bbid}:`, requestBody);

        const response = await fetch(config.api.availabilityWorker, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });

        console.log(`Month availability response status for BBID ${bbid}:`, response.status);

        if (response.ok) {
          const data = await response.json();
          console.log(`Month availability data for BBID ${bbid}:`, data);
          return { bbid, data };
        } else {
          console.log(`Month availability response not ok for BBID ${bbid}:`, response.status);
          return { bbid, data: null };
        }
      });

      const results = await Promise.all(availabilityPromises);
      console.log('All month availability results:', results);
      
      // Combine all successful results
      const successfulResults = results.filter(result => result.data && result.data.success);
      
      if (successfulResults.length > 0) {
        // Store combined data
        const combinedData = {
          success: true,
          data: {
            roomtypes: successfulResults.flatMap(result => 
              result.data.data?.roomtypes?.map(room => ({
                ...room,
                bbid: result.bbid
              })) || []
            )
          }
        };
        
        setMonthAvailability(prev => ({
          ...prev,
          [monthKey]: combinedData
        }));
        
        // Process blocked dates from all properties
        if (combinedData.data?.roomtypes) {
          console.log('Processing roomtypes for blocked dates from all properties:', combinedData.data.roomtypes);
          const blocked = {};
          const dateAvailability = {};
          
          // First, collect availability for each date from all properties
          combinedData.data.roomtypes.forEach(room => {
            console.log('Processing room:', room.rtid, 'from BBID:', room.bbid, 'availability:', room.availability);
            if (room.availability && Array.isArray(room.availability)) {
              room.availability.forEach((day, index) => {
                const date = new Date(firstDay);
                date.setDate(firstDay.getDate() + index);
                const dateKey = date.toISOString().split('T')[0];
                
                if (!dateAvailability[dateKey]) {
                  dateAvailability[dateKey] = [];
                }
                dateAvailability[dateKey].push(day.roomsfree);
                console.log(`Date ${dateKey}, day ${index}: roomsfree = ${day.roomsfree}`);
              });
            }
          });
          
          console.log('Date availability summary from all properties:', dateAvailability);
          
          // Then, block dates only if ANY room from ANY property is unavailable
          Object.keys(dateAvailability).forEach(dateKey => {
            const anyRoomUnavailable = dateAvailability[dateKey].some(available => !available);
            console.log(`Date ${dateKey}: anyRoomUnavailable = ${anyRoomUnavailable}`);
            if (anyRoomUnavailable) {
              blocked[dateKey] = true;
              console.log('Blocked date:', dateKey, 'Some rooms unavailable');
            }
          });
          
          console.log('Setting blocked dates from all properties:', blocked);
          setBlockedDates(prev => ({ ...prev, ...blocked }));
        } else {
          console.log('No roomtypes data in combined response');
        }
      } else {
        console.log('No successful month availability results');
      }
    } catch (error) {
      console.log('Could not fetch month availability:', error);
    }
  }, [monthAvailability, config.datePicker.propertyBBIDs, config.api.availabilityWorker]);

  // Check availability when month changes
  useEffect(() => {
    checkMonthAvailability(currentMonth.getMonth(), currentMonth.getFullYear());
  }, [currentMonth, checkMonthAvailability]);

  // Debug blocked dates state
  useEffect(() => {
    console.log('Current blocked dates:', blockedDates);
  }, [blockedDates]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="check-availability-widget">
      <div className="ca-app">
        {/* Header */}
        <header className="ca-app-header">
          <div className="ca-header-content">
            <div className="ca-logo">
              <h1>{config.header.logo}</h1>
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              className="ca-mobile-menu-button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              <span className={`ca-hamburger-line ${isMobileMenuOpen ? 'ca-open' : ''}`}></span>
              <span className={`ca-hamburger-line ${isMobileMenuOpen ? 'ca-open' : ''}`}></span>
              <span className={`ca-hamburger-line ${isMobileMenuOpen ? 'ca-open' : ''}`}></span>
            </button>
            
            {/* Desktop Navigation */}
            <nav className="ca-nav-menu ca-desktop-nav">
              {config.header.navigation.map((item, index) => (
                <a key={index} href={item.href}>{item.label}</a>
              ))}
            </nav>
            <button className="ca-cta-button ca-desktop-cta">{config.header.ctaButton.text}</button>
            
            {/* Mobile Navigation */}
            <nav className={`ca-nav-menu ca-mobile-nav ${isMobileMenuOpen ? 'ca-mobile-nav-open' : ''}`}>
              {config.header.navigation.map((item, index) => (
                <a 
                  key={index} 
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <button className="ca-cta-button ca-mobile-cta">{config.header.ctaButton.text}</button>
            </nav>
          </div>
        </header>



      {/* Full Width Content Block */}
      <section className="full-width-section">
        <div className="full-width-content">
          <h2>{config.datePicker.title}</h2>
          <div className="ca-date-picker-container">
            <div className="ca-modern-date-picker" ref={calendarRef}>
              <div 
                className="ca-date-input-wrapper"
                onClick={() => setShowCalendar(!showCalendar)}
              >
                <input
                  type="text"
                  className="ca-modern-date-input"
                  value={dateRange}
                  readOnly
                  placeholder={config.datePicker.placeholder}
                />
                <div className="ca-calendar-icon">📅</div>
                {selectedStartDate && (
                  <button 
                    className="ca-clear-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedStartDate(null);
                      setSelectedEndDate(null);
                      setDateRange('');
                    }}
                  >
                    {config.datePicker.clearButtonText}
                  </button>
                )}
              </div>
              
              {showCalendar && (
                <div 
                  className="ca-fixed ca-inset-0 ca-bg-black ca-bg-opacity-50 flex items-center justify-center z-50"
                  onClick={() => {
                    setShowCalendar(false);
                    // Clear cached month data when calendar is closed
                    setMonthAvailability({});
                    setBlockedDates({});
                    console.log('Calendar closed - cleared cached data');
                  }}
                >
                  <div 
                    className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={prevMonth}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                        </svg>
                      </button>
                      <h3 className="text-lg font-semibold">
                        {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                      </h3>
                      <button
                        onClick={nextMonth}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                        </svg>
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center text-sm font-medium text-gray-500 py-1">
                          {day}
                        </div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-7 gap-1 h-64">
                      {days.map((day, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            if (day && !isDateDisabled(day)) {
                              handleDateClick(day);
                              // Update the date range display
                              if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
                                // First selection or new selection
                                setDateRange(`${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`);
                              } else {
                                // Second selection (checkout date) - close calendar and start search
                                const start = `${selectedStartDate.getFullYear()}-${String(selectedStartDate.getMonth() + 1).padStart(2, '0')}-${String(selectedStartDate.getDate()).padStart(2, '0')}`;
                                const end = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
                                setDateRange(`${start} - ${end}`);
                                setShowCalendar(false); // Close calendar when checkout date is selected
                              }
                            }
                          }}
                          disabled={isDateDisabled(day)}
                          className={`
                            p-2 text-sm rounded hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center h-8
                            ${!day ? 'invisible' : ''}
                            ${isDateDisabled(day) ? 'text-gray-300 cursor-not-allowed bg-gray-100' : 'text-gray-700 hover:text-blue-600'}
                            ${isDateSelected(day) ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
                            ${isDateInRange(day) && !isDateSelected(day) ? 'bg-blue-100 text-blue-700' : ''}
                          `}
                        >
                          {day ? day.getDate() : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Date Summary */}
          {selectedStartDate && selectedEndDate && (
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center space-x-4 text-sm">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  <span className="text-blue-800 font-medium">
                    {formatDate(`${selectedStartDate.getFullYear()}-${String(selectedStartDate.getMonth() + 1).padStart(2, '0')}-${String(selectedStartDate.getDate()).padStart(2, '0')}`)} - {formatDate(`${selectedEndDate.getFullYear()}-${String(selectedEndDate.getMonth() + 1).padStart(2, '0')}-${String(selectedEndDate.getDate()).padStart(2, '0')}`)}
                  </span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span className="text-green-800 font-medium">
                    {getNightsCount()} night{getNightsCount() !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoadingAvailability && (
            <div className="text-center py-6 bg-blue-50 rounded-lg border border-blue-200">
              <div className="inline-flex items-center mb-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                <span className="text-blue-800 font-medium text-lg">
                  {isFetching ? 'Checking room availability...' : 'Loading availability data...'}
                </span>
              </div>
              <p className="text-blue-600 text-sm">
                This may take a few seconds.
              </p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span className="text-red-800 font-medium text-lg">Error loading availability data</span>
                </div>
                <button
                  onClick={() => {
                    setError(null);
                    if (selectedStartDate && selectedEndDate) {
                      fetchAvailability(selectedStartDate.toISOString().split('T')[0], selectedEndDate.toISOString().split('T')[0]);
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
              <p className="text-red-700 text-sm">We couldn't connect to the availability service.</p>
            </div>
          )}

          {/* Availability Display */}
          {availabilityData && !isLoadingAvailability && !error && (
            <div>
              <div className="availability-debug">
                {(() => {
                  // Handle error cases with proper string conversion
                  if (availabilityData.error) {
                    const errorText = typeof availabilityData.error === 'object' 
                      ? JSON.stringify(availabilityData.error) 
                      : String(availabilityData.error);
                    
                    // Check if it's an error object with code
                    if (typeof availabilityData.error === 'object' && availabilityData.error.code !== undefined) {
                      const errorCode = availabilityData.error.code;
                      
                      // If success is true and error code is 0, this means no error
                      if (availabilityData.success && errorCode === 0) {
                        // This is actually a successful response, not an error
                        const { data } = availabilityData;
                        if (!data || !data.roomtypes) {
                          return <div>No room data available</div>;
                        }

                        return (
                          <div>
                            <h4>Available Rooms from Multiple Properties</h4>
                            
                            <div className="rooms-list">
                              {data.roomtypes.map((room, index) => {
                                const isAvailable = room.availability && Array.isArray(room.availability) 
                                  ? room.availability.every(day => day.roomsfree === true)
                                  : true;
                                
                                // Only show rooms that are available for all days
                                if (!isAvailable) {
                                  return null;
                                }

                                // Get content data for this room if available
                                const contentRoom = contentData?.data?.roomtypes?.find(contentRoom => 
                                  contentRoom.roomtypeid === room.rtid || contentRoom.rtid === room.rtid
                                );
                                
                                // Get image data for this room if available
                                const roomImageData = roomImages[room.rtid];
                                const primaryImage = roomImageData && roomImageData.length > 0 ? roomImageData[0] : null;
                                
                                // Get image URL with fallbacks for different formats
                                const getImageUrl = (image) => {
                                  if (!image) return null;
                                  // Try different possible URL formats, prioritize medium for better visibility
                                  return image.medium || image.small || image.big || image.url || image.src || image;
                                };
                                
                                const imageUrl = primaryImage ? getImageUrl(primaryImage) : null;
                                
                                return (
                                  <div key={index} className="room-card" onClick={() => openBookingIframe(room.bbid, room.rtid)}>
                                    <div className="room-content-wrapper">
                                      {/* Room Image Section */}
                                      <div className="room-image-section">
                                        {imageUrl ? (
                                          <img 
                                            src={imageUrl} 
                                            alt={contentRoom?.roomtypename || room.rtname || 'Room'} 
                                            className="room-image"
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.nextSibling.style.display = 'block';
                                            }}
                                          />
                                        ) : (
                                          <div className="room-image-placeholder">
                                            <span>📷</span>
                                            <p>No image available</p>
                                          </div>
                                        )}
                                        {imageUrl && (
                                          <div className="room-image-fallback" style={{ display: 'none' }}>
                                            <span>📷</span>
                                            <p>Image unavailable</p>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Room Info Section */}
                                      <div className="room-info-section">
                                        <div className="room-header">
                                          <h5 className="room-title">{String(contentRoom?.roomtypename || room.rtname || 'Unknown Room')}</h5>
                                          <span className="availability-badge available">
                                            Available
                                          </span>
                                        </div>
                                        
                                        <div className="room-info">
                                          <p><strong>Property:</strong> {String(room.propertyName || `BBID: ${room.bbid}` || 'Unknown Property')}</p>
                                          <p><strong>Room ID:</strong> {String(room.rtid || 'N/A')}</p>
                                          <p><strong>Max Occupancy:</strong> {String(contentRoom?.maxoccupancy || room.maxoccupancy || 'N/A')} guests</p>
                                          <p><strong>Description:</strong> {String(contentRoom?.description || 'No description available')}</p>
                                          
                                          {room.rates && room.rates.length > 0 && (
                                            <p><strong>Rates:</strong> R{room.rates.join(', R')} per night</p>
                                          )}

                                          {contentRoom?.amenities && contentRoom.amenities.length > 0 && (
                                            <div className="room-amenities">
                                              <p><strong>Amenities:</strong></p>
                                              <div className="amenities-list">
                                                {contentRoom.amenities.slice(0, 3).map((amenity, idx) => (
                                                  <span key={idx} className="amenity-tag">
                                                    {amenity.description}
                                                  </span>
                                                ))}
                                                {contentRoom.amenities.length > 3 && (
                                                  <span className="amenity-tag more">
                                                    +{contentRoom.amenities.length - 3} more
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                        
                                        <div className="room-booking-hint" style={{ 
                                          marginTop: '10px', 
                                          padding: '8px', 
                                          backgroundColor: '#f0f8ff', 
                                          borderRadius: '4px',
                                          fontSize: '0.9em',
                                          color: '#0066cc'
                                        }}>
                                          💳 Click to book this specific room
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                    }
                    
                    return <div>API Error: {errorText}</div>;
                  }
                  
                  // Handle successful response
                  if (availabilityData.success && availabilityData.data) {
                    const { data } = availabilityData;
                    if (!data.roomtypes) {
                      return <div>No room data available</div>;
                    }

                    return (
                      <div>
                        <h4>Available Rooms from Multiple Properties</h4>
                        
                        <div className="rooms-list">
                          {data.roomtypes.map((room, index) => {
                            const isAvailable = room.availability && Array.isArray(room.availability) 
                              ? room.availability.every(day => day.roomsfree === true)
                              : true;
                            
                            // Only show rooms that are available for all days
                            if (!isAvailable) {
                              return null;
                            }

                            // Get content data for this room if available
                            const contentRoom = contentData?.data?.roomtypes?.find(contentRoom => 
                              contentRoom.roomtypeid === room.rtid || contentRoom.rtid === room.rtid
                            );
                            
                            // Get image data for this room if available
                            const roomImageData = roomImages[room.rtid];
                            const primaryImage = roomImageData && roomImageData.length > 0 ? roomImageData[0] : null;
                            
                            // Get image URL with fallbacks for different formats
                            const getImageUrl = (image) => {
                              if (!image) return null;
                              // Try different possible URL formats, prioritize medium for better visibility
                              return image.medium || image.small || image.big || image.url || image.src || image;
                            };
                            
                            const imageUrl = primaryImage ? getImageUrl(primaryImage) : null;
                            
                            return (
                              <div key={index} className="room-card" onClick={() => openBookingIframe(room.bbid, room.rtid)}>
                                <div className="room-content-wrapper">
                                  {/* Room Image Section */}
                                  <div className="room-image-section">
                                    {imageUrl ? (
                                      <img 
                                        src={imageUrl} 
                                        alt={contentRoom?.roomtypename || room.rtname || 'Room'} 
                                        className="room-image"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'block';
                                        }}
                                      />
                                    ) : (
                                      <div className="room-image-placeholder">
                                        <span>📷</span>
                                        <p>No image available</p>
                                      </div>
                                    )}
                                    {imageUrl && (
                                      <div className="room-image-fallback" style={{ display: 'none' }}>
                                        <span>📷</span>
                                        <p>Image unavailable</p>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Room Info Section */}
                                  <div className="room-info-section">
                                    <div className="room-header">
                                      <h5 className="room-title">{String(contentRoom?.roomtypename || room.rtname || 'Unknown Room')}</h5>
                                      <span className="availability-badge available">
                                        Available
                                      </span>
                                    </div>
                                    
                                    <div className="room-info">
                                      <p><strong>Property:</strong> {String(room.propertyName || `BBID: ${room.bbid}` || 'Unknown Property')}</p>
                                      <p><strong>Room ID:</strong> {String(room.rtid || 'N/A')}</p>
                                      <p><strong>Max Occupancy:</strong> {String(contentRoom?.maxoccupancy || room.maxoccupancy || 'N/A')} guests</p>
                                      <p><strong>Description:</strong> {String(contentRoom?.description || 'No description available')}</p>
                                      
                                      {room.rates && room.rates.length > 0 && (
                                        <p><strong>Rates:</strong> R{room.rates.join(', R')} per night</p>
                                      )}

                                      {contentRoom?.amenities && contentRoom.amenities.length > 0 && (
                                        <div className="room-amenities">
                                          <p><strong>Amenities:</strong></p>
                                          <div className="amenities-list">
                                            {contentRoom.amenities.slice(0, 3).map((amenity, idx) => (
                                              <span key={idx} className="amenity-tag">
                                                {amenity.description}
                                              </span>
                                            ))}
                                            {contentRoom.amenities.length > 3 && (
                                              <span className="amenity-tag more">
                                                +{contentRoom.amenities.length - 3} more
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    
                                    <div className="room-booking-hint" style={{ 
                                      marginTop: '10px', 
                                      padding: '8px', 
                                      backgroundColor: '#f0f8ff', 
                                      borderRadius: '4px',
                                      fontSize: '0.9em',
                                      color: '#0066cc'
                                    }}>
                                      💳 Click to book this specific room
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                  
                  return <div>Unexpected data format</div>;
                })()}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <h2>{config.features.title}</h2>
        <div className="features-grid">
          {config.features.items.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Booking Iframe Modal */}
      {showIframe && (
        <div className="ca-fixed ca-inset-0 ca-bg-black ca-bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-6xl w-full mx-4 h-screen">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Book Your Stay</h3>
              <button
                onClick={() => setShowIframe(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <div className="h-full" style={{ height: 'calc(100vh - 120px)' }}>
              <iframe
                src={iframeUrl}
                className="w-full h-full border-0"
                title="NightsBridge Booking"
                allow="payment"
                style={{ minHeight: '600px' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="ca-app-footer">
        <div className="ca-footer-content">
          <p>{config.footer.copyright}</p>
        </div>
      </footer>
        </div>
    </div>
  );
}

export default App;
